import { NoopProvider } from "./providers/NoopProvider";

/**
 * @typedef {Object} AnalyticsProvider
 * @property {string} name
 * @property {(config?: any) => Promise<AnalyticsProvider>} init
 * @property {() => boolean} isReady
 * @property {(userId: string, traits?: Record<string, any>) => void} identify
 * @property {() => void} reset
 * @property {(eventName: string, properties?: Record<string, any>) => void} track
 * @property {(traits: Record<string, any>) => void} setPersonProperties
 * @property {() => string | null} getSessionId
 * @property {() => string | null} getSessionReplayUrl
 */

/**
 * @typedef {Object} UploadIdentity
 * @property {string} uploadId
 * @property {string} [userId]
 * @property {string} [sessionId]
 */

const MAX_QUEUE_SIZE = 500;

/**
 * AnalyticsService is the single public surface for analytics across the app.
 * No feature code should import a provider directly.
 *
 * Responsibilities:
 *   - Hide the provider behind a stable API
 *   - Buffer events emitted before the async provider is ready (bounded queue)
 *   - Re-apply the last identify() call whenever the provider is swapped
 *   - Provide upload-specific convenience methods that stamp uploadId+context
 *
 * The service is designed to be **safe to import from anywhere**: constructing
 * with NoopProvider means all methods are non-throwing no-ops until wired.
 */
export class AnalyticsService {
  /**
   * @param {AnalyticsProvider} [provider]
   */
  constructor(provider = new NoopProvider()) {
    /** @type {AnalyticsProvider} */
    this._provider = provider;
    /** @type {Array<{ kind: string, args: any[], ts: number }>} */
    this._queue = [];
    /** @type {UploadIdentity | null} */
    this._identity = null;
    /**
     * Cumulative person properties (user profile facets). Applied via
     * provider.setPersonProperties on every swap so PostHog's Person Profiles
     * stay in sync.
     * @type {Record<string, any>}
     */
    this._personProperties = {};
    /**
     * Cumulative super properties (stamped on every event by PostHog's
     * register() API). This is where environment / frontendVersion / commitSha
     * belong so events are filterable in Insights without joining across the
     * person profile.
     * @type {Record<string, any>}
     */
    this._superProperties = {};
    /** @type {boolean} */
    this._enabled = true;
    /** @type {(event: string, props: any) => void | null} */
    this._eventHook = null;
  }

  /**
   * Swap the active provider — called by bootstrap() when the real provider
   * finishes loading. Re-applies identity so PostHog Person Profiles link up,
   * then drains the queue in order.
   *
   * @param {AnalyticsProvider} provider
   */
  setProvider(provider) {
    this._provider = provider;
    if (this._identity) {
      this._provider.identify(this._identity.userId, this._identity.traits);
    }
    if (Object.keys(this._personProperties).length > 0) {
      this._provider.setPersonProperties?.(this._personProperties);
    }
    if (Object.keys(this._superProperties).length > 0) {
      this._provider.registerSuperProperties?.(this._superProperties);
    }
    // Publish the current provider's name on window so the admin settings
    // page can compare against the desired state and prompt a reload when
    // they diverge (2026-08-06). Without this the "Reload required" banner
    // was dead code — nothing was ever writing here.
    try {
      if (typeof window !== "undefined") {
        window.__clipn_analytics_provider_name = provider?.name || null;
      }
    } catch (_error) {
      /* ignore — pure diagnostic side-effect */
    }
    this._drainQueue();
  }

  /** Get the active provider (mostly for tests). */
  getProvider() {
    return this._provider;
  }

  /**
   * Wire a listener that receives every tracked event *before* it is dispatched
   * to the provider. Used by the diagnostics timeline so analytics events also
   * land in the per-upload event history without a second call.
   *
   * @param {(event: string, props: any) => void} hook
   */
  setEventHook(hook) {
    this._eventHook = typeof hook === "function" ? hook : null;
  }

  /** Optional kill switch (e.g. GDPR opt-out). Not currently wired to UI. */
  setEnabled(enabled) {
    this._enabled = enabled !== false;
  }

  /**
   * Attach identity to the current PostHog user profile.
   * Safe to call before the provider is ready — the identity is remembered and
   * re-applied on setProvider().
   *
   * @param {string} userId
   * @param {Record<string, any>} [traits]
   */
  identify(userId, traits) {
    if (!userId) return;
    this._identity = { userId, traits: traits || {} };
    this._safeCall(() => this._provider.identify(userId, traits));
  }

  /** Clear identity — call on signOut. */
  reset() {
    this._identity = null;
    this._personProperties = {};
    // NOTE: super properties intentionally survive reset(). They describe the
    // environment (which user X signed out of), not the user themselves.
    // Wiping them on signOut would leave anonymous events untagged.
    this._safeCall(() => this._provider.reset());
  }

  /**
   * Set person-profile properties (user-facing attributes surfaced in the
   * PostHog Person view). Cumulative — merge semantics.
   */
  setPersonProperties(traits) {
    if (!traits) return;
    this._personProperties = { ...this._personProperties, ...traits };
    this._safeCall(() => this._provider.setPersonProperties(traits));
  }

  /**
   * Register super properties — key/value pairs stamped on every subsequent
   * event by the provider (PostHog uses localStorage-backed register()).
   * The right home for environment tag, frontend version, and any other
   * stable event-level metadata that should be filterable in Insights.
   *
   * Cumulative + re-applied whenever the provider is swapped.
   */
  registerSuperProperties(traits) {
    if (!traits) return;
    this._superProperties = { ...this._superProperties, ...traits };
    this._safeCall(() =>
      this._provider.registerSuperProperties?.(traits)
    );
  }

  /**
   * Primary event API. `properties` is shallow-merged with baseline context
   * (frontend version, session id) so every event carries diagnostic tags.
   *
   * @param {string} eventName
   * @param {Record<string, any>} [properties]
   */
  track(eventName, properties = {}) {
    if (!this._enabled || !eventName) return;
    const enriched = this._enrich(properties);
    if (this._eventHook) {
      try {
        this._eventHook(eventName, enriched);
      } catch (_error) {
        // never let analytics observers break analytics
      }
    }
    if (!this._provider.isReady()) {
      this._enqueue("track", [eventName, enriched]);
      return;
    }
    this._safeCall(() => this._provider.track(eventName, enriched));
  }

  /**
   * Convenience helper for upload lifecycle events. Every upload event is
   * namespaced with `upload_` and stamped with the uploadId so PostHog cohorts
   * / funnels can be built without ad-hoc property filters.
   *
   * @param {string} uploadId
   * @param {string} eventName            e.g. "started", "chunk_completed"
   * @param {Record<string, any>} [properties]
   */
  captureUploadEvent(uploadId, eventName, properties = {}) {
    if (!uploadId || !eventName) return;
    const namespaced = eventName.startsWith("upload_")
      ? eventName
      : `upload_${eventName}`;
    this.track(namespaced, { uploadId, ...properties });
  }

  /**
   * Convenience for terminal failure — attaches a full diagnostic snapshot so
   * the PostHog event carries everything needed to reproduce (without asking
   * the user for logs).
   *
   * @param {string} uploadId
   * @param {Record<string, any>} snapshot   see DiagnosticSnapshot.build()
   */
  captureUploadFailure(uploadId, snapshot) {
    if (!uploadId) return;
    this.track("upload_failed", {
      uploadId,
      ...snapshot,
    });
  }

  /** @returns {string | null} */
  getSessionId() {
    return this._safeCall(() => this._provider.getSessionId()) ?? null;
  }

  /** @returns {string | null} */
  getSessionReplayUrl() {
    return this._safeCall(() => this._provider.getSessionReplayUrl()) ?? null;
  }

  // ── internals ─────────────────────────────────────────────────────────────

  _enrich(properties) {
    // Fallback tags for providers that don't have server-side super properties
    // (ConsoleProvider, NoopProvider). PostHog handles this via register()
    // already, so these are redundant but harmless on that provider.
    const base = {
      $frontend_version: this._superProperties.frontendVersion,
      $environment: this._superProperties.environment,
      $ph_session_id: this.getSessionId?.() || undefined,
    };
    return { ...base, ...properties };
  }

  _enqueue(kind, args) {
    if (this._queue.length >= MAX_QUEUE_SIZE) {
      this._queue.shift();
    }
    this._queue.push({ kind, args, ts: Date.now() });
  }

  _drainQueue() {
    if (this._queue.length === 0) return;
    const drained = this._queue;
    this._queue = [];
    for (const entry of drained) {
      if (entry.kind === "track") {
        this._safeCall(() =>
          this._provider.track(entry.args[0], entry.args[1])
        );
      }
    }
  }

  _safeCall(fn) {
    try {
      return fn();
    } catch (_error) {
      // Analytics failures must never break the app. Swallow silently — the
      // provider may itself log to console.error.
      return null;
    }
  }
}
